import { createResource } from 'react-resource-router';

import { FeatureKeys } from '@atlassian/bitbucket-features';

import { mergeChecksResourceGetDataApi } from 'src/components/settings/merge-checks';
import {
  BranchConfigKey,
  EnabledMergeChecks,
  CheckModulesConfig,
} from 'src/components/settings/merge-checks/types';
import { makeBranchConfigKey } from 'src/components/settings/merge-checks/utils';
import { ResourceContext } from 'src/router/types';
import urls from 'src/sections/repository/urls';
import { getFeatures } from 'src/selectors/feature-selectors';

export const mergeChecksResource = createResource<CheckModulesConfig>({
  type: 'merge-checks',
  getKey: ({ match }) => {
    const { repositoryOwner, repositorySlug } = match.params;
    return `${repositoryOwner}/${repositorySlug}`;
  },
  maxAge: 60000, // cache for 1 minute so navigating between pages is fast
  getData: async ({ match }, { reduxStore }: ResourceContext) => {
    const { repositoryOwner, repositorySlug } = match.params;

    if (!repositoryOwner || !repositorySlug) {
      throw new Error(
        'This resource must be rendered on a route that includes both :repositoryOwner and :repositorySlug'
      );
    }

    const state = reduxStore.getState();
    if (getFeatures(state)[FeatureKeys.useCustomMergeCheckV2Apis]) {
      throw new Error('Using V2 configuration APIs');
    }

    const emptyEnabledChecksMap = new Map<
      BranchConfigKey,
      EnabledMergeChecks
    >();
    if (!getFeatures(state)[FeatureKeys.extensibleMergeChecksEnabled]) {
      return {
        installedForgeCheckModules: [],
        enabledChecksMap: emptyEnabledChecksMap,
      };
    }

    const { getMergeCheckModules } = mergeChecksResourceGetDataApi(
      urls.api.internal.mergeChecks(repositoryOwner, repositorySlug)
    );

    const configResponse = await getMergeCheckModules();

    // Convert CheckModulesConfigResponse to CheckModulesConfig to make it easier to work with in UI
    const enabledChecksMap = configResponse.branchConfigs.reduce((acc, val) => {
      const { enabledChecks, ...everythingElse } = val;
      // This looks a little weird due to nesting, but everythingElse.type is the BranchConfig.
      acc.set(makeBranchConfigKey(everythingElse.type), {
        branchConfig: everythingElse.type,
        checkIds: new Map(enabledChecks.map(v => [v.extensionId, v.required])),
      });
      return acc;
    }, emptyEnabledChecksMap);

    return {
      installedForgeCheckModules: configResponse.installedForgeModules,
      enabledChecksMap,
    };
  },
});
